import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, KeyRound, FileSignature, Receipt, RefreshCw } from "lucide-react";

export function PlansAdminTab() {
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const load = () => Promise.all([
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}),
    api.get("/modules").then((r) => setModules(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-8" data-testid="admin-plans">
      <div>
        <h3 className="font-heading text-xl text-slate-900 mb-3">Plan Templates</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div key={p.plan_id} className="sk-card p-6">
              <div className="flex items-center justify-between">
                <div className="font-heading text-lg text-slate-900 capitalize">{p.name}</div>
                <span className="text-xs text-slate-500 font-mono">{p.plan_id}</span>
              </div>
              <div className="mt-2 text-sm text-slate-500">{p.description}</div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-slate-500 uppercase">Setup</div><div className="font-mono">${p.setup_fee.toFixed(2)}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Min / mo</div><div className="font-mono">${p.monthly_minimum.toFixed(2)}</div></div>
              </div>
              <div className="mt-4">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Modules ({p.modules.length})</div>
                <div className="flex flex-wrap gap-1">
                  {p.modules.map((m) => (
                    <span key={m} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">{m}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-xl text-slate-900 mb-3">Module Catalog</h3>
        <div className="sk-card overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <div className="col-span-3">Module</div>
            <div className="col-span-3">Description</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-1 text-right">Mo.</div>
            <div className="col-span-1 text-right">%</div>
            <div className="col-span-2 text-right">Flat / tx</div>
          </div>
          {modules.map((m) => (
            <div key={m.module_id} className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-slate-200">
              <div className="col-span-3">
                <div className="text-slate-900 font-medium">{m.name}</div>
                <div className="text-xs text-slate-500 font-mono">{m.module_id}</div>
              </div>
              <div className="col-span-3 text-slate-600 text-xs">{m.description}</div>
              <div className="col-span-2 text-slate-500 capitalize">{m.category}</div>
              <div className="col-span-1 text-right font-mono">${m.default_pricing.monthly.toFixed(2)}</div>
              <div className="col-span-1 text-right font-mono">{m.default_pricing.percent_rate.toFixed(2)}%</div>
              <div className="col-span-2 text-right font-mono">${m.default_pricing.flat_per_tx.toFixed(2)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function BillingAdminTab({ users }) {
  const [selected, setSelected] = useState("");
  const [data, setData] = useState(null);
  const [modules, setModules] = useState([]);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/modules").then((r) => setModules(r.data)),
      api.get("/plans").then((r) => setPlans(r.data)),
      api.get("/admin/invoices").then((r) => setInvoices(r.data)),
    ]).catch(() => {});
  }, []);

  const loadFor = async (uid) => {
    setSelected(uid);
    if (!uid) { setData(null); return; }
    const { data } = await api.get(`/admin/billing/${uid}`);
    setData(data);
  };

  const setField = (k, v) => setData((d) => ({ ...d, billing: { ...d.billing, [k]: v } }));

  const toggleModule = (mid) => {
    setData((d) => {
      const has = d.billing.modules.includes(mid);
      const modules = has ? d.billing.modules.filter((x) => x !== mid) : [...d.billing.modules, mid];
      return { ...d, billing: { ...d.billing, modules } };
    });
  };

  const setPriceOverride = (mid, key, value) => {
    setData((d) => {
      const pricing = { ...(d.billing.pricing || {}) };
      pricing[mid] = { ...(pricing[mid] || {}), [key]: parseFloat(value) || 0 };
      return { ...d, billing: { ...d.billing, pricing } };
    });
  };

  const save = async () => {
    try {
      const b = data.billing;
      await api.post(`/admin/billing/${selected}`, {
        plan_id: b.plan_id,
        custom: !!b.custom,
        modules: b.modules,
        pricing: b.pricing || {},
        payment_method: b.payment_method || "ach_pull",
        payment_last4: b.payment_last4 || null,
        setup_fee_override: b.setup_fee_override == null || b.setup_fee_override === "" ? null : parseFloat(b.setup_fee_override),
        monthly_minimum_override: b.monthly_minimum_override == null || b.monthly_minimum_override === "" ? null : parseFloat(b.monthly_minimum_override),
        notes: b.notes || null,
      });
      toast.success("Billing saved");
      loadFor(selected);
    } catch (e) { toast.error("Save failed"); }
  };

  const generateInvoice = async () => {
    try {
      const { data: inv } = await api.post(`/admin/billing/${selected}/invoice`);
      toast.success(`Invoice ${inv.invoice_id} generated — $${inv.total.toFixed(2)}`);
      const { data: list } = await api.get("/admin/invoices");
      setInvoices(list);
    } catch (e) { toast.error("Could not generate invoice"); }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/admin/invoices/${id}/mark-paid`);
      toast.success("Invoice paid");
      const { data: list } = await api.get("/admin/invoices");
      setInvoices(list);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="admin-billing">
      <div className="sk-card p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-heading text-lg text-slate-900">Per-Merchant Billing</h3>
            <p className="text-sm text-slate-500 mt-1">Pick a merchant — defaults inherit from their plan, custom rates override.</p>
          </div>
          <div className="w-full sm:w-80">
            <Select value={selected} onValueChange={loadFor}>
              <SelectTrigger className="bg-white border-slate-200 h-11" data-testid="admin-billing-user-select"><SelectValue placeholder="Select a merchant…" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900 max-h-72">
                {users.filter((u) => u.role === "merchant").map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.email} — {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {data && (
        <div className="sk-card p-6 space-y-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-700">Plan</Label>
              <Select value={data.billing.plan_id} onValueChange={(v) => setField("plan_id", v)}>
                <SelectTrigger className="mt-2 bg-white border-slate-200 h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-900">
                  {plans.map((p) => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Setup fee override (USD)</Label>
              <Input type="number" value={data.billing.setup_fee_override ?? ""} onChange={(e) => setField("setup_fee_override", e.target.value)} className="mt-2 bg-white border-slate-200 h-11" placeholder="(default)" />
            </div>
            <div>
              <Label className="text-slate-700">Monthly minimum override (USD)</Label>
              <Input type="number" value={data.billing.monthly_minimum_override ?? ""} onChange={(e) => setField("monthly_minimum_override", e.target.value)} className="mt-2 bg-white border-slate-200 h-11" placeholder="(default)" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-slate-700">Modules &amp; pricing overrides</Label>
              <label className="text-xs text-slate-500 inline-flex items-center gap-2">
                <input type="checkbox" checked={!!data.billing.custom} onChange={(e) => setField("custom", e.target.checked)} />
                Mark as custom pricing
              </label>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 px-3 py-2 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                <div className="col-span-1">On</div>
                <div className="col-span-3">Module</div>
                <div className="col-span-2 text-right">Monthly</div>
                <div className="col-span-2 text-right">% rate</div>
                <div className="col-span-2 text-right">Flat / tx</div>
                <div className="col-span-2 text-right">Default</div>
              </div>
              {modules.map((m) => {
                const enabled = data.billing.modules.includes(m.module_id);
                const ov = (data.billing.pricing || {})[m.module_id] || {};
                return (
                  <div key={m.module_id} className="grid grid-cols-12 items-center px-3 py-2 text-sm border-b border-slate-200">
                    <div className="col-span-1">
                      <input type="checkbox" checked={enabled} onChange={() => toggleModule(m.module_id)} data-testid={`mod-toggle-${m.module_id}`} />
                    </div>
                    <div className="col-span-3">
                      <div className="text-slate-900 text-sm">{m.name}</div>
                      <div className="font-mono text-[11px] text-slate-500">{m.module_id}</div>
                    </div>
                    <div className="col-span-2"><Input value={ov.monthly ?? ""} onChange={(e) => setPriceOverride(m.module_id, "monthly", e.target.value)} className="h-9 text-right font-mono bg-white" placeholder={m.default_pricing.monthly.toFixed(2)} /></div>
                    <div className="col-span-2"><Input value={ov.percent_rate ?? ""} onChange={(e) => setPriceOverride(m.module_id, "percent_rate", e.target.value)} className="h-9 text-right font-mono bg-white" placeholder={m.default_pricing.percent_rate.toFixed(2)} /></div>
                    <div className="col-span-2"><Input value={ov.flat_per_tx ?? ""} onChange={(e) => setPriceOverride(m.module_id, "flat_per_tx", e.target.value)} className="h-9 text-right font-mono bg-white" placeholder={m.default_pricing.flat_per_tx.toFixed(2)} /></div>
                    <div className="col-span-2 text-right text-xs text-slate-400 font-mono">${m.default_pricing.monthly} · {m.default_pricing.percent_rate}% · ${m.default_pricing.flat_per_tx}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button onClick={save} className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold h-11" data-testid="admin-billing-save">Save billing config</Button>
            <Button onClick={generateInvoice} variant="ghost" className="border border-[#0E2148]/30 text-[#0E2148] hover:bg-[#0E2148]/5 h-11" data-testid="admin-billing-invoice">
              <Receipt className="w-4 h-4 mr-2" /> Generate invoice now
            </Button>
          </div>
        </div>
      )}

      <div className="sk-card overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-heading text-lg text-slate-900">All Invoices</h3>
          <span className="text-xs text-slate-500 font-mono">{invoices.length} total</span>
        </div>
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
          <div className="col-span-3">Invoice</div>
          <div className="col-span-3">Merchant</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Total</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
        {invoices.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No invoices yet.</div>}
        {invoices.map((inv) => {
          const u = users.find((x) => x.user_id === inv.user_id);
          return (
            <div key={inv.invoice_id} className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-slate-200">
              <div className="col-span-3 font-mono text-xs text-slate-600">{inv.invoice_id}</div>
              <div className="col-span-3 text-slate-700 truncate">{u?.email || inv.user_id}</div>
              <div className="col-span-2">
                <span className={`px-2 py-0.5 rounded text-xs uppercase tracking-wider ${inv.status === "paid" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{inv.status}</span>
              </div>
              <div className="col-span-2 text-right font-mono">${inv.total.toFixed(2)}</div>
              <div className="col-span-2 text-right">
                {inv.status !== "paid" && (
                  <Button size="sm" variant="ghost" className="border border-slate-300 hover:bg-slate-100 text-xs" onClick={() => markPaid(inv.invoice_id)} data-testid={`mark-paid-${inv.invoice_id}`}>Mark paid</Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SystemAdminTab() {
  const [status, setStatus] = useState(null);
  const [llm, setLlm] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/system/status");
      setStatus(data);
    } catch { toast.error("Status fetch failed"); }
  };

  const runLlm = async () => {
    setRunning(true); setLlm(null);
    try {
      const { data } = await api.post("/admin/system/test-llm");
      setLlm(data);
      if (data.ok) toast.success("LLM connected"); else toast.error(data.error || "LLM test failed");
    } catch { toast.error("LLM test failed"); }
    finally { setRunning(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="admin-system">
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="sk-card p-6" data-testid="system-llm-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#C8A046]" />
              <span className="text-xs uppercase tracking-wider text-slate-500">AI / LLM</span>
            </div>
            {llm && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${llm.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                {llm.ok ? "Healthy" : "Failed"}
              </span>
            )}
          </div>
          <div className="mt-3 text-slate-700 text-sm">
            Risk &amp; AML scoring uses <span className="font-mono">claude-sonnet-4-5-20250929</span> via the Emergent universal LLM key.
          </div>
          {llm && llm.ok && (
            <div className="mt-4 p-3 rounded bg-slate-50 border border-slate-200 text-sm space-y-1">
              <div className="text-xs uppercase tracking-wider text-slate-500">Live response ({llm.latency_seconds}s)</div>
              <div className="text-slate-900 italic">"{llm.response}"</div>
            </div>
          )}
          {llm && !llm.ok && (
            <div className="mt-4 p-3 rounded bg-red-50 border border-red-200 text-sm text-red-700 font-mono">
              {llm.error}
            </div>
          )}
          <Button onClick={runLlm} disabled={running} className="mt-4 bg-[#00D533] hover:bg-[#00E636] text-black font-semibold h-10" data-testid="system-test-llm-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${running ? "animate-spin" : ""}`} />
            {running ? "Testing..." : "Run LLM connectivity test"}
          </Button>
        </div>

        <div className="sk-card p-6" data-testid="system-gateway-card">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#0E2148]" />
            <span className="text-xs uppercase tracking-wider text-slate-500">Transaction Gateway (PCI)</span>
          </div>
          <div className="mt-2 text-slate-900 font-mono">{status?.gateway?.url}</div>
          <div className="mt-1 text-sm text-slate-500">{status?.gateway?.pci_level}</div>
          <a
            href={status?.gateway?.url} target="_blank" rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1 text-sm text-[#0E2148] hover:text-[#C8A046] font-semibold"
          >
            Open gateway portal
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 3h7v7m0-7L10 14M5 5h4v2H7v10h10v-2h2v4H5V5z"/></svg>
          </a>
        </div>
      </div>

      <div className="sk-card overflow-hidden" data-testid="system-secrets-card">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-slate-500" />
          <h3 className="font-heading text-lg text-slate-900">Secrets uploader status</h3>
          <span className="ml-auto text-xs text-slate-500 font-mono">
            {status?.secrets?.filter((s) => s.present).length} / {status?.secrets?.length || 0} present
          </span>
        </div>
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
          <div className="col-span-4">Key</div>
          <div className="col-span-2">Present</div>
          <div className="col-span-6 font-mono">Preview</div>
        </div>
        {status?.secrets?.map((s) => (
          <div key={s.key} className="grid grid-cols-12 items-center px-4 py-2.5 text-sm border-b border-slate-200">
            <div className="col-span-4 font-mono text-slate-900">{s.key}</div>
            <div className="col-span-2">
              {s.present ? (
                <span className="px-2 py-0.5 rounded text-xs uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">yes</span>
              ) : (
                <span className="px-2 py-0.5 rounded text-xs uppercase tracking-wider bg-red-50 text-red-700 border border-red-200">missing</span>
              )}
            </div>
            <div className="col-span-6 font-mono text-slate-500">{s.preview || "—"}</div>
          </div>
        ))}
      </div>

      <div className="sk-card p-6 grid sm:grid-cols-3 gap-4 text-sm">
        <Mini label="Stripe mode" value={status?.stripe_mode || "—"} />
        <Mini label="Fraud module pricing" value={status?.fraud_module ? `$${status.fraud_module.default_pricing.monthly}/mo + $${status.fraud_module.default_pricing.flat_per_tx}/tx` : "—"} />
        <Mini label="Backend" value="FastAPI · Mongo · supervisor" />
      </div>
    </div>
  );
}
function Mini({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-slate-900 font-mono">{value}</div>
    </div>
  );
}
